import { ErrorHandler, Injectable, inject } from "@angular/core";
import { I18n } from "src/ancestors/i18n/i18n.module";

@Injectable()
export class TranslateService {
  private i18n = inject(I18n);
  private errorHandler = inject(ErrorHandler);
  private _translation?: Record<string, string>;
  private _currentLocale?: string;

  constructor() {
    this.loadTranslation();
  }

  public loadTranslation(): void {
    this.i18n.getLocale().then((t) => {
      this._translation = t.default as Record<string, string>; // eslint-disable-line
      this._currentLocale = this.i18n.locale;
    }).catch((error: unknown) => {
      this.errorHandler.handleError(error);
      // Fallback to empty translations if loading fails
      this._translation = {};
    });
  }

  /**
   * Reload translations for the current locale
   * This should be called when the locale changes at runtime
   */
  public async reloadTranslations(): Promise<void> {
    try {
      const translations = await this.i18n.getLocale();
      this._translation = translations.default;
      this._currentLocale = this.i18n.locale;
      await this.i18n.setLocale();
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      // Keep existing translations if reload fails
    }
  }

  /**
   * Check if translations need to be reloaded due to locale change
   * This is useful when the locale changes at runtime
   */
  public checkAndReloadIfNeeded(): void {
    if (this._currentLocale !== this.i18n.locale) {
      this.loadTranslation();
    }
  }

  /**
   * Passando una stringa o un array di stringhe ritorna la/le traduzioni
   * 
   * @param translateKey Chiave della traduzione
   * @param textTransform Possibilita di trasformare il testo in upperCase, lowerCase o capitalize
   * @returns Traduzione
   */
  public instant<T>(translateKey: string | string[], options?: { transform?: "upper-case" | "lower-case" | "capitalize", arg?: { [k: string]: string } }): T {
    // Check if translations need to be reloaded
    this.checkAndReloadIfNeeded();
    
    if (Array.isArray(translateKey)) {
      return translateKey.map((key: string) => this.translation?.[key] ?? key)
      .filter(Boolean) as T;
    }
    
    if (!this.translation?.[translateKey]) {
      return translateKey as T;
    }
    
    let returnMsg: string = this.translation[translateKey];
    if (options?.arg) {
      for (const key in options.arg) {
        returnMsg = returnMsg.split(`{{${key}}}`).join(options.arg[key]);
      }
    }
    // Applica transform sul testo già interpolato con `arg`, non sulla stringa grezza della chiave.
    if (options?.transform === "upper-case") {
      returnMsg = returnMsg.toUpperCase();
    } else if (options?.transform === "lower-case") {
      returnMsg = returnMsg.toLowerCase();
    } else if (options?.transform === "capitalize") {
      returnMsg =
        returnMsg.length > 0 ? `${returnMsg[0].toUpperCase()}${returnMsg.slice(1).toLowerCase()}` : returnMsg;
    }

    return returnMsg as T;
  }

  public has(translateKey?: string | string[]): boolean {
    if (!translateKey) return false;

    // Check if translations need to be reloaded
    this.checkAndReloadIfNeeded();

    if (Array.isArray(translateKey)) {
      return translateKey.map((key: string) => this.translation?.[key])
        .every(Boolean);
    }

    return this.translation?.[translateKey] ? true : false;
  }

  /**
   * Oggetto traduzioni da usare nelle pipe
   */
  public get translation() {
    return this._translation;
  }

  /**
   * Get the current locale
   */
  public get currentLocale(): string | undefined {
    return this._currentLocale;
  }
}